import { useEffect } from 'react'
import { useParams } from 'react-router-dom'
import { Link as RouterLink } from 'react-router-dom'
import {
  Alert,
  Box,
  Breadcrumbs,
  Card,
  CardContent,
  Skeleton,
  Typography,
} from '@mui/material'
import Link from '@mui/material/Link'
import { Home as HomeIcon, NavigateNext as NavigateNextIcon } from '@mui/icons-material'
import { useTranslation } from 'react-i18next'
import { DashboardLayout } from '@/components/layout/DashboardLayout'
import { ErrorPage } from '@/components/common/ErrorPage'
import { PageHelpPopover } from '@/components/common/PageHelpPopover'
import { ContractFinanceSection } from '@/components/billing/ContractFinanceSection'
import { useUserStore } from '@/stores/userStore'
import { ROUTES } from '@/utils/constants'
import { getUserDisplayName } from '@/utils/userName'
import '@/styles/dashboard.css'

export function UserFinancePage() {
  const { id } = useParams<{ id: string }>()
  const { t } = useTranslation()
  const { currentUser, isLoading, error, errorStatus, fetchUser, clearError } = useUserStore()

  useEffect(() => {
    if (id) {
      fetchUser(parseInt(id)).catch((err) => {
        console.error('Failed to fetch user:', err)
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id])

  if (isLoading) {
    return (
      <DashboardLayout>
        <Box sx={{ p: 3 }}>
          <Skeleton variant="text" width={300} height={32} sx={{ mb: 2 }} />
          <Skeleton variant="text" width={200} height={40} sx={{ mb: 3 }} />
          <Card>
            <CardContent>
              <Skeleton variant="text" width="40%" height={32} sx={{ mb: 2 }} />
              <Skeleton variant="rectangular" height={220} sx={{ borderRadius: 2 }} />
            </CardContent>
          </Card>
        </Box>
      </DashboardLayout>
    )
  }

  if (!currentUser) {
    return (
      <ErrorPage
        fullPage
        statusCode={errorStatus ?? 500}
        message={error ?? undefined}
      />
    )
  }

  const studentDisplayName = getUserDisplayName(currentUser, { prefer: 'full_name' })

  return (
    <DashboardLayout>
      <Box sx={{ p: 3 }}>
        <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} aria-label="breadcrumb" sx={{ mb: 2 }}>
          <Link component={RouterLink} to={ROUTES.DASHBOARD} underline="hover" sx={{ display: 'flex', alignItems: 'center' }}>
            <HomeIcon sx={{ mr: 0.5 }} fontSize="inherit" />
            {t('dashboard.home')}
          </Link>
          <Link component={RouterLink} to={ROUTES.USERS} underline="hover">
            {t('dashboard.students')}
          </Link>
          <Link component={RouterLink} to={`${ROUTES.USERS}/${currentUser.id}`} underline="hover">
            {studentDisplayName || t('profile.title')}
          </Link>
          <Typography color="text.primary">{t('billing.financeSection', 'Финансы')}</Typography>
        </Breadcrumbs>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3, flexWrap: 'wrap' }}>
          <Typography variant="h4" component="h1" fontWeight={700} sx={{ flexGrow: 1 }}>
            {t('billing.financeSection', 'Финансы')}
          </Typography>
          <PageHelpPopover pageKey="user-finance" />
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={clearError}>
            {error}
          </Alert>
        )}

        {currentUser.contract ? (
          <ContractFinanceSection contractId={currentUser.contract.id} studentId={currentUser.id} />
        ) : (
          <Alert severity="info">{t('billing.noContract', 'Договор не найден')}</Alert>
        )}
      </Box>
    </DashboardLayout>
  )
}
